import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';

const Context = createContext();

export const StateContext = ({ children }) => {
  const [showCart, setShowCart] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalQuantities, setTotalQuantities] = useState(0);
  const [qty, setQty] = useState(1);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [currencyWarning, setCurrencyWarning] = useState(null);

  const CART_STORAGE_KEY = 'bridgenapp_cart_items';

  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem(CART_STORAGE_KEY);
    if (savedCart) {
      const { cartItems, totalPrice, totalQuantities } = JSON.parse(savedCart);
      setCartItems(cartItems);
      setTotalPrice(totalPrice);
      setTotalQuantities(totalQuantities);
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    if (cartItems.length > 0) {
      const cartData = { cartItems, totalPrice, totalQuantities };
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartData));
    }
  }, [cartItems, totalPrice, totalQuantities]);

  const checkCurrencyConsistency = (product) => {
    if (cartItems.length === 0) return true;
    const cartCurrency = cartItems[0].currency || 'TZS';
    const newCurrency = product.currency || 'TZS';
    return cartCurrency === newCurrency;
  };

  const onAdd = (product, quantity, index) => {
    if (!checkCurrencyConsistency(product)) {
      setCurrencyWarning(`Cannot mix currencies. Current cart is in ${cartItems[0]?.currency || 'TZS'}`);
      toast.error(`Cannot add ${product.name}. Currency mismatch!`);
      return { success: false };
    }

    setCurrencyWarning(null);
    const checkProductInCart = cartItems.find((item) => item._id === product._id);

    setTotalPrice((prev) => prev + product.price * quantity);
    setTotalQuantities((prev) => prev + quantity);

    if (checkProductInCart) {
      const updatedCartItems = cartItems.map((cartProduct) => 
        cartProduct._id === product._id 
          ? { ...cartProduct, quantity: cartProduct.quantity + quantity, selectedIndex: index }
          : cartProduct
      );
      setCartItems(updatedCartItems);
    } else {
      setCartItems([...cartItems, { ...product, quantity, selectedIndex: index }]);
    }

    toast.success(`${quantity} ${product.name} added to cart`);
    return { success: true };
  };

  const onRemove = (product) => {
    const foundProduct = cartItems.find((item) => item._id === product._id);
    const newCartItems = cartItems.filter((item) => item._id !== product._id);

    setTotalPrice((prev) => prev - foundProduct.price * foundProduct.quantity);
    setTotalQuantities((prev) => prev - foundProduct.quantity);
    setCartItems(newCartItems);
    toast.success(`${foundProduct.name} removed from cart`);
  };

  const toggleCartItemQuantity = (id, value) => {
    const updatedCartItems = cartItems.map((item) => 
      item._id === id 
        ? { ...item, quantity: value === 'inc' ? item.quantity + 1 : Math.max(item.quantity - 1, 1) }
        : item
    );

    const newTotalPrice = updatedCartItems.reduce((acc, item) => acc + item.price * item.quantity, 0);
    const newTotalQuantities = updatedCartItems.reduce((acc, item) => acc + item.quantity, 0);

    setCartItems(updatedCartItems);
    setTotalPrice(newTotalPrice);
    setTotalQuantities(newTotalQuantities);
  };

  const clearCart = () => {
    setCartItems([]);
    setTotalPrice(0);
    setTotalQuantities(0);
    localStorage.removeItem(CART_STORAGE_KEY);
    toast.success('Cart cleared');
  };

  const incQty = () => setQty((prev) => prev + 1);
  const decQty = () => setQty((prev) => (prev - 1 < 1 ? 1 : prev - 1));

  return (
    <Context.Provider
      value={{
        showCart,
        setShowCart,
        cartItems,
        totalPrice,
        totalQuantities,
        qty,
        incQty,
        decQty,
        onAdd,
        toggleCartItemQuantity,
        onRemove,
        setCartItems,
        setTotalPrice,
        setTotalQuantities,
        selectedIndex,
        updateSelectedIndex: setSelectedIndex,
        clearCart,
        currencyWarning,
        setCurrencyWarning
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useStateContext = () => useContext(Context);